import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { __GLOBAL_SESSION_ID } from "@/client/globals";

import { findNextSchedule } from "./utils";

/** A single scheduled check with its ISO date string. */
export interface ScheduleEntry {
  id: string;
  scheduledAt: string;
}

export interface SentimentCheckSliceState {
  /** Assessment open date */
  openDate: string | null;
  /** Date of the next Sentiment Check */
  date: string | null;
  /** Whether the sentiment check date has passed */
  passed: boolean;
  /** Sorted list of all scheduled check dates for this assessment. */
  schedules: ScheduleEntry[];
  /** Previous session ID when the last sentiment check was done. */
  prevSessionId: string | null;
  /** Whether students are allowed to skip a check. */
  skipEnabled: boolean;
}

export const initialState: SentimentCheckSliceState = {
  openDate: null,
  date: null,
  passed: false,
  schedules: [],
  prevSessionId: null,
  skipEnabled: true,
};

/** Payload to initialise sentiment check state. */
interface SetSentimentCheckDataPayload {
  /** Assessment open date */
  openDate: string | null;
  /** Sorted schedule entries from the backend. */
  schedules: ScheduleEntry[];
  /** Session ID of the last sentiment check. */
  prevSessionId: string | null;
  /** Date of the last sentiment check. */
  lastCheckDate: string | null;
  /** Whether students are allowed to skip a check. */
  skipEnabled: boolean;
}

export const sentimentCheckSlice = createSlice({
  name: "sentimentCheck",
  initialState,
  reducers: {
    /** Init the sentiment checker state using schedule entries. */
    setSentimentCheckData(
      state,
      action: PayloadAction<SetSentimentCheckDataPayload>
    ) {
      const { openDate, schedules, prevSessionId, lastCheckDate, skipEnabled } =
        action.payload;

      state.openDate = openDate;
      state.schedules = schedules;
      state.skipEnabled = skipEnabled;

      // Preserve prevSessionId if it already matches the current session
      // (i.e. a check was submitted this session, e.g. from the home page).
      // Otherwise, initialise from the database value.
      if (state.prevSessionId !== __GLOBAL_SESSION_ID.current) {
        state.prevSessionId = prevSessionId;
      }

      const nextDate = findNextSchedule(schedules, lastCheckDate);
      if (nextDate) {
        state.date = nextDate;
        state.passed = false;
      } else {
        state.passed = true;
      }
    },
    /** Advance to the next scheduled check after a submission. */
    nextSentimentCheck(state, action: PayloadAction<{ date: string }>) {
      state.date = action.payload.date;
      state.prevSessionId = __GLOBAL_SESSION_ID.current;
    },
    /** Mark all sentiment checks as done. */
    endSentimentCheck(state) {
      state.passed = true;
    },
  },
});

export const { setSentimentCheckData, nextSentimentCheck, endSentimentCheck } =
  sentimentCheckSlice.actions;

export const sentimentCheckReducer = sentimentCheckSlice.reducer;
